import React, {memo, ReactNode} from 'react';
import styled from 'styled-components';

import {buildHoverParentClassName} from '../../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {Plural} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {FrontEvent, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownItemProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {InteractiveListItem} from '../../../../../libs/shared/ui/src/components/interactiveList/interactiveListItem';
import {isTeammateNamespace} from '../../../../helpers/models/namespaceHelpers';
import {AnswerFolderModel} from '../../../../models/api/answerFolderModel';
import {TeamModel} from '../../../../models/api/teamModel';
import {
  renderDescriptionWithBreadcrumbs,
  StyledAnswersDropdownItemDiv,
  StyledCircleSeperatorSpan,
} from './answersDropdownRow';
import {AnswersDropdownRowContent} from './answersDropdownRowContent';

/*
 * Props.
 */

interface AnswersFolderDropdownRowProps extends DropdownItemProps {
  folder: AnswerFolderModel;
  onClick: HandlerOf<FrontEvent>;
  folderBreadcrumbs: ReadonlyArray<ReactNode>;
  isSearchResult: boolean;
  showTeamAvatar: boolean;
  folderTeam?: TeamModel;
}

/*
 * Styles.
 */

const StyledFolderDescriptionDetailsSpan = styled.span`
  display: flex;
  align-items: center;
`;

/*
 * Component.
 */

export const AnswersFolderDropdownRow = memo<AnswersFolderDropdownRowProps>((props) => {
  const isPrivateFolder = isTeammateNamespace(props.folder.namespace);
  const folderDescriptionDetails = renderDescriptionDetails(props.folder);

  return (
    <InteractiveListItem onSelect={props.onClick} index={props.index}>
      {({itemProps, isHighlighted}) => (
        <StyledAnswersDropdownItemDiv
          {...itemProps} /* eslint-disable-line react/jsx-props-no-spreading */
          className={buildHoverParentClassName(isHighlighted, props.className)}
          $isHighlighted={isHighlighted}
        >
          <AnswersDropdownRowContent
            title={props.folder.name}
            description={
              props.isSearchResult
                ? renderDescriptionWithBreadcrumbs(folderDescriptionDetails, props.folderBreadcrumbs)
                : renderDescription(folderDescriptionDetails)
            }
            iconName="folderSmall"
            isPrivate={isPrivateFolder}
            showTeamAvatar={props.showTeamAvatar}
            team={props.folderTeam}
            showRightChevron
          />
        </StyledAnswersDropdownItemDiv>
      )}
    </InteractiveListItem>
  );
});

function renderDescription(folderDescriptionDetails: ReactNode) {
  return <StyledFolderDescriptionDetailsSpan>{folderDescriptionDetails}</StyledFolderDescriptionDetailsSpan>;
}

function renderDescriptionDetails(folder: AnswerFolderModel) {
  return (
    <>
      <Plural
        id="canned-response-folder-desc-num-folders"
        value={folder.numChildFolders}
        one={`${folder.numChildFolders} folder`}
        other={`${folder.numChildFolders} folders`}
      />
      <StyledCircleSeperatorSpan />
      <Plural
        id="canned-response-folder-desc-num-responses"
        value={folder.numChildAnswers}
        one={`${folder.numChildAnswers} template`}
        other={`${folder.numChildAnswers} templates`}
      />
    </>
  );
}
